import ResponsePicker from './ResponsePicker';

const DETRIMENTAL_TYPES = [
  'Cemetery',
  'Commercial (describe)',
  'Gas Station',
  'Landfill',
  'Noise (describe)',
  'Other (specify)',
  'Towers (communication, high voltage)',
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────

// For groups stored as { key: bool } objects, set exactly one to true
function radioGroup(obj, key) {
  return Object.keys(obj).reduce((acc, k) => ({ ...acc, [k]: k === key }), {});
}

function RadioRow({ label, group, value, options, onChange }) {
  return (
    <div className="rt-radio-row">
      <span className="rt-radio-row-label">{label}</span>
      <div className="rt-check-group">
        {options.map(([key, display]) => (
          <label key={key} className="rt-check">
            <input
              type="radio"
              checked={value === key}
              onChange={() => onChange(radioGroup(group, key))}
            />
            {display}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function NeighbourhoodTab({ neighbourhood: nbhd, onChange }) {
  const set    = (k, v)  => onChange({ ...nbhd, [k]: v });
  const setLU  = (k, v)  => onChange({ ...nbhd, land_use:          { ...nbhd.land_use,          [k]: v } });
  const setMC  = updated => onChange({ ...nbhd, market_conditions: updated });
  const setBU  = updated => onChange({ ...nbhd, built_up:          updated });

  const activeMC = Object.keys(nbhd.market_conditions).find(k => nbhd.market_conditions[k]) ?? null;
  const activeBU = Object.keys(nbhd.built_up).find(k => nbhd.built_up[k]) ?? null;

  const appendComment = (text) => {
    const current = nbhd.comments || '';
    set('comments', current ? `${current}\n\n${text}` : text);
  };

  return (
    <div className="rt-body">

      {/* ── A: LAND USE ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Land Use</h3>
        <div className="rt-two-col">
          <div className="rt-check-group" style={{ flexDirection: 'column', gap: 10 }}>
            {[
              ['residential',  'Residential'],
              ['commercial',   'Commercial'],
              ['industrial',   'Industrial'],
              ['agricultural', 'Agricultural'],
              ['first_nations','First Nations / Indigenous Land'],
            ].map(([key, label]) => (
              <label key={key} className="rt-check">
                <input type="checkbox" checked={nbhd.land_use[key] ?? false}
                  onChange={e => setLU(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
          <div className="rt-check-group" style={{ flexDirection: 'column', gap: 10 }}>
            {[
              ['urban',        'Urban'],
              ['suburban',     'Suburban'],
              ['rural',        'Rural'],
              ['recreational', 'Recreational / Resort'],
              ['forestry',     'Forestry / Public / Park'],
            ].map(([key, label]) => (
              <label key={key} className="rt-check">
                <input type="checkbox" checked={nbhd.land_use[key] ?? false}
                  onChange={e => setLU(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* ── B: MARKET CONDITIONS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Market Conditions</h3>
        <div className="rt-check-group">
          {[
            ['improving',     'Improving'],
            ['stable',        'Stable'],
            ['transitioning', 'Transitioning'],
            ['deteriorating', 'Deteriorating'],
          ].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="radio" checked={activeMC === key}
                onChange={() => setMC(radioGroup(nbhd.market_conditions, key))} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* ── C: BUILT UP ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Built Up</h3>
        <div className="rt-check-group">
          {[
            ['over_75',       'Over 75%'],
            ['between_25_75', '25 – 75%'],
            ['under_25',      'Under 25%'],
          ].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="radio" checked={activeBU === key}
                onChange={() => setBU(radioGroup(nbhd.built_up, key))} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* ── D: SUBJECT CONFORMITY ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Subject Conformity</h3>

        <div className="rt-field">
          <label>Subject Typical for Neighbourhood</label>
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio" checked={nbhd.subject_typical === true}
                onChange={() => set('subject_typical', true)} />
              Yes
            </label>
            <label className="rt-check">
              <input type="radio" checked={nbhd.subject_typical === false}
                onChange={() => set('subject_typical', false)} />
              No (see comments)
            </label>
          </div>
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label className="rt-check">
            <input type="checkbox" checked={nbhd.detrimental_observed ?? false}
              onChange={e => {
                set('detrimental_observed', e.target.checked);
                if (!e.target.checked) set('detrimental_type', '');
              }} />
            Detrimental Conditions Observed
          </label>
        </div>

        {nbhd.detrimental_observed && (
          <div className="rt-field" style={{ marginTop: 8 }}>
            <label>Detrimental Type</label>
            <select value={nbhd.detrimental_type ?? ''}
              onChange={e => set('detrimental_type', e.target.value)}>
              <option value="">Select…</option>
              {DETRIMENTAL_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* ── E: RANGES ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Ranges</h3>

        <div className="rt-row">
          <div className="rt-field">
            <label>Age Range — From (years)</label>
            <input type="text" value={nbhd.age_range_from ?? ''}
              onChange={e => set('age_range_from', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Age Range — To (years)</label>
            <input type="text" value={nbhd.age_range_to ?? ''}
              onChange={e => set('age_range_to', e.target.value)} />
          </div>
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>Price Range — From</label>
            <div className="rt-prefix-input">
              <span className="rt-prefix">$</span>
              <input type="text" value={nbhd.price_range_from ?? ''}
                onChange={e => set('price_range_from', e.target.value)} />
            </div>
          </div>
          <div className="rt-field">
            <label>Price Range — To</label>
            <div className="rt-prefix-input">
              <span className="rt-prefix">$</span>
              <input type="text" value={nbhd.price_range_to ?? ''}
                onChange={e => set('price_range_to', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* ── F: MARKET OVERVIEW ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Market Overview</h3>

        <RadioRow
          label="Supply"
          group={{ high: false, average: false, low: false }}
          value={nbhd.market_supply}
          options={[['high','High'],['average','Average'],['low','Low']]}
          onChange={updated => set('market_supply', Object.keys(updated).find(k => updated[k]) ?? '')}
        />
        <RadioRow
          label="Demand"
          group={{ high: false, average: false, low: false }}
          value={nbhd.market_demand}
          options={[['high','High'],['average','Average'],['low','Low']]}
          onChange={updated => set('market_demand', Object.keys(updated).find(k => updated[k]) ?? '')}
        />
        <RadioRow
          label="Price Trends"
          group={{ increasing: false, stable: false, declining: false }}
          value={nbhd.price_trends}
          options={[['increasing','Increasing'],['stable','Stable'],['declining','Declining']]}
          onChange={updated => set('price_trends', Object.keys(updated).find(k => updated[k]) ?? '')}
        />
      </div>

      {/* ── G: SOURCE + COMMENTS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Source &amp; Comments</h3>

        <div className="rt-field">
          <label>Market Data Source / Reference</label>
          <input type="text" value={nbhd.source_of_market_data ?? ''}
            placeholder="e.g. MLS, CREA, Local Board Data…"
            onChange={e => set('source_of_market_data', e.target.value)} />
        </div>

        <div className="rt-field">
          <div className="rt-field-label-row">
            <label>Neighbourhood Comments</label>
            <ResponsePicker
              category="neighbourhood"
              onInsert={appendComment}
            />
          </div>
          <textarea rows={8} value={nbhd.comments ?? ''}
            onChange={e => set('comments', e.target.value)} />
        </div>
      </div>

    </div>
  );
}
